// src/lib/transfers.ts
import { supabase } from "@/lib/supabase";

export async function createTransfer({
  from_account_id,
  to_account_id,
  amount,
  date,
  description,
  user_id,
  attachment_url,
  category_id,
}: {
  from_account_id: string;
  to_account_id: string;
  amount: number;
  date: string;
  description: string;
  user_id: string;
  attachment_url: string;
  category_id: string;
}) {
  const { data: transfer, error: transferError } = await supabase
    .from("transfers")
    .insert({
      from_account_id,
      to_account_id,
      amount,
      date,
      description,
      user_id,
      attachment_url,
    })
    .select()
    .single();

  if (transferError || !transfer) {
    console.error("❌ Error al crear transferencia:", transferError?.message);
    return null;
  }

  const transactionsPayload = [
    {
      account_id: from_account_id,
      type: "expense",
      amount,
      date,
      description: `${description}`,
      transfer_id: transfer.id,
      user_id,
      attachment_url,
      category_id,
    },
    {
      account_id: to_account_id,
      type: "income",
      amount,
      date,
      description: `${description}`,
      transfer_id: transfer.id,
      user_id,
      attachment_url,
      category_id,
    },
  ];

  const { error: txError } = await supabase
    .from("transactions")
    .insert(transactionsPayload);

  if (txError) {
    console.error("❌ Error al crear transacciones:", txError.message);
    return null;
  }

  return transfer;
}

export async function updateTransfer({
  transfer_id,
  amount,
  date,
  description,
  attachment_url,
  category_id,
}: {
  transfer_id: string;
  amount: number;
  date: string;
  description: string;
  attachment_url: string;
  category_id: string;
}) {
  const { error: transferError } = await supabase
    .from("transfers")
    .update({ amount, date, description, attachment_url, category_id })
    .eq("id", transfer_id);

  if (transferError) {
    console.error(
      "❌ Error al actualizar transferencia:",
      transferError.message
    );
    return false;
  }

  const { data: transactions, error: fetchError } = await supabase
    .from("transactions")
    .select("id, type, account_id")
    .eq("transfer_id", transfer_id);

  if (fetchError || !transactions || transactions.length !== 2) {
    console.error("❌ Error obteniendo transacciones:", fetchError?.message);
    return false;
  }

  const updates = transactions.map((tx) => ({
    id: tx.id,
    update: {
      amount,
      date,
      description:
        tx.type === "expense"
          ? `${description} (a ${
              transactions.find((t) => t.type === "income")?.account_id
            })`
          : `${description} (desde ${
              transactions.find((t) => t.type === "expense")?.account_id
            })`,
    },
  }));

  for (const tx of updates) {
    const { error: updateError } = await supabase
      .from("transactions")
      .update(tx.update)
      .eq("id", tx.id);

    if (updateError) {
      console.error("❌ Error actualizando transacción:", updateError.message);
      return false;
    }
  }

  return true;
}

export async function deleteTransactionOrTransfer(tx: any) {
  if (tx.transfer_id) {
    const { error: txError } = await supabase
      .from("transactions")
      .delete()
      .eq("transfer_id", tx.transfer_id);

    const { error: transferError } = await supabase
      .from("transfers")
      .delete()
      .eq("id", tx.transfer_id);

    if (txError || transferError) {
      console.error(
        "Error al eliminar transferencia:",
        txError || transferError
      );
      return false;
    }
  } else {
    const { error } = await supabase
      .from("transactions")
      .delete()
      .eq("id", tx.id);

    if (error) {
      console.error("Error al eliminar transacción:", error.message);
      return false;
    }
  }

  return true;
}
